import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Shield,
  KeyRound,
  Fingerprint,
  Loader2,
  Volume2,
  VolumeX,
} from "lucide-react";

const VIDEO_URL = "/background-web.mp4";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const videoRef = useRef(null);

  const [discordEnabled, setDiscordEnabled] = useState(false);
  const [needsFounder, setNeedsFounder] = useState(false);

  const [step, setStep] = useState("main");
  // main | invite | discord | hex | bootstrap

  const [inviteCode, setInviteCode] = useState("");
  const [inviteRole, setInviteRole] = useState(null);

  const [mockDiscordId, setMockDiscordId] = useState("");
  const [mockUsername, setMockUsername] = useState("");

  const [discordData, setDiscordData] = useState(null);

  const [hexId, setHexId] = useState("");
  const [displayName, setDisplayName] = useState("");

  const [loading, setLoading] = useState(false);

  // Video / audio
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(0.5);

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    api
      .get("/auth/discord/config")
      .then((r) => setDiscordEnabled(!!r.data.enabled))
      .catch(() => {});

    api
      .get("/auth/system-status")
      .then((r) => {
        if (!r.data.founder_exists) {
          setNeedsFounder(true);
          setStep("bootstrap");
        }
      })
      .catch(() => {});
  }, []);

  /*
   * Video hazır olduğunda başlangıç ses seviyesini ayarla.
   */
  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.volume = volume;
    videoRef.current.muted = true;
  }, []);

  /*
   * Sesi aç / kapat
   */
  const toggleMute = () => {
    if (!videoRef.current) return;

    const nextMuted = !muted;

    videoRef.current.muted = nextMuted;

    if (!nextMuted) {
      videoRef.current.volume = volume > 0 ? volume : 0.5;

      if (volume === 0) {
        setVolume(0.5);
      }
    }

    setMuted(nextMuted);
  };

  /*
   * Ses seviyesi
   */
  const handleVolumeChange = (e) => {
    const nextVolume = Number(e.target.value);

    setVolume(nextVolume);

    if (!videoRef.current) return;

    videoRef.current.volume = nextVolume;

    if (nextVolume === 0) {
      videoRef.current.muted = true;
      setMuted(true);
    } else {
      videoRef.current.muted = false;
      setMuted(false);
    }
  };

  /*
   * Discord OAuth
   */
  const goDiscord = async () => {
    setLoading(true);

    try {
      const res = await api.get("/auth/discord/login-url");

      window.location.href = res.data.url;
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Discord OAuth henüz yapılandırılmadı"
      );

      setLoading(false);
    }
  };

  /*
   * Invite doğrulama
   */
  const verifyInvite = async () => {
    if (!inviteCode.trim()) {
      return toast.error("Davet kodu girin");
    }

    setLoading(true);

    try {
      const res = await api.post("/invites/verify", {
        code: inviteCode.trim().toUpperCase(),
      });

      setInviteRole(res.data.initial_role);
      setStep("discord");

      toast.success("Davet kodu geçerli");
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Doğrulama başarısız"
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Mock Discord Login
   */
  const mockDiscordLogin = async () => {
    if (!mockDiscordId.trim() || !mockUsername.trim()) {
      return toast.error("Discord ID ve kullanıcı adı gerekli");
    }

    setLoading(true);

    try {
      const res = await api.post("/auth/mock-discord-login", {
        discord_id: mockDiscordId.trim(),
        discord_username: mockUsername.trim(),
        discord_avatar: `https://api.dicebear.com/9.x/identicon/svg?seed=${mockDiscordId}`,
      });

      if (res.data.registered) {
        login(res.data.token, res.data.user);

        toast.success("Giriş başarılı");

        navigate("/");
      } else {
        setDiscordData(res.data);
        setStep("hex");
      }
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Discord girişi başarısız"
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Kayıt tamamla
   */
  const completeRegistration = async () => {
    if (!hexId.trim()) {
      return toast.error("Hex ID gerekli");
    }

    setLoading(true);

    try {
      const res = await api.post("/auth/register", {
        invite_code: inviteCode.trim().toUpperCase(),
        discord_id: discordData.discord_id,
        discord_username: discordData.discord_username,
        discord_avatar: discordData.discord_avatar,
        hex_id: hexId.trim(),
        display_name: displayName.trim() || null,
      });

      login(res.data.token, res.data.user);

      toast.success("Kayıt tamamlandı");

      navigate("/");
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Kayıt başarısız"
      );
    } finally {
      setLoading(false);
    }
  };

  /*
   * Founder bootstrap
   */
  const bootstrapFounder = async () => {
    if (
      !mockDiscordId.trim() ||
      !mockUsername.trim() ||
      !hexId.trim()
    ) {
      return toast.error("Tüm alanlar gerekli");
    }

    setLoading(true);

    try {
      const res = await api.post(
        "/auth/bootstrap-founder",
        {
          invite_code: "BOOTSTRAP",
          discord_id: mockDiscordId.trim(),
          discord_username: mockUsername.trim(),
          discord_avatar: `https://api.dicebear.com/9.x/identicon/svg?seed=${mockDiscordId}`,
          hex_id: hexId.trim(),
          display_name: mockUsername.trim(),
        }
      );

      login(res.data.token, res.data.user);

      toast.success("Kurucu hesabı oluşturuldu");

      navigate("/");
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Kurulum başarısız"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-black">

      {/* =========================================================
          BACKGROUND VIDEO
      ========================================================== */}

      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="fixed inset-0 w-full h-full object-cover"
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* =========================================================
          VIDEO OVERLAYS
      ========================================================== */}

      {/* Ana karartma */}
      <div className="fixed inset-0 bg-black/55 pointer-events-none" />

      {/* Üstten alta gradient */}
      <div className="fixed inset-0 bg-gradient-to-br from-black/80 via-black/35 to-rose-950/60 pointer-events-none" />

      {/* Alt karartma */}
      <div className="fixed inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

      {/* Hafif grid */}
      <div className="fixed inset-0 grid-lines opacity-20 pointer-events-none" />

      {/* =========================================================
          TOP BRAND
      ========================================================== */}

      <div className="fixed top-6 left-6 md:top-8 md:left-8 z-30">
        <div className="flex items-center gap-3">

          <div className="w-11 h-11 md:w-12 md:h-12 bg-rose-600 flex items-center justify-center font-heading font-bold text-white text-xl md:text-2xl shadow-lg shadow-rose-900/30">
            D
          </div>

          <div>
            <div className="font-heading font-bold text-lg md:text-xl tracking-tight text-white">
              DEULSRA GRUP
            </div>

            <div className="text-[9px] md:text-xs font-mono text-rose-400/80 tracking-widest">
              FIVEM // COMMAND CENTER
            </div>
          </div>

        </div>
      </div>

      {/* =========================================================
          MAIN CONTENT
      ========================================================== */}

      <div className="relative z-10 min-h-screen flex items-center justify-center p-5 md:p-8">

        <div className="w-full max-w-md">

          {/* =====================================================
              MAIN LOGIN
          ====================================================== */}

          {step === "main" && (
            <Card className="bg-zinc-950/10 backdrop-blur-xl border-white/10 shadow-2xl shadow-black/10 p-7 md:p-8 space-y-6">

              <div>
                <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">
                  // AUTHENTICATE
                </div>

                <h2 className="font-heading text-3xl font-bold tracking-tight text-white">
                  Giriş
                </h2>

                <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
                  Discord ile devam et. İlk kez katılıyorsan
                  yetkiden davet kodu al.
                </p>
              </div>

              <Button
                onClick={goDiscord}
                disabled={loading || !discordEnabled}
                data-testid="discord-oauth-button"
                className="w-full bg-[#5865F2] hover:bg-[#4752c4] h-12 font-semibold text-base"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Shield className="w-5 h-5 mr-2" />
                )}

                Discord ile Giriş Yap
              </Button>

              {!discordEnabled && (
                <div className="text-[11px] text-amber-400/80 bg-amber-950/30 border-l-2 border-amber-500/60 p-3 font-mono">
                  Discord OAuth henüz kurulmadı. Admin'in{" "}
                  <span className="text-amber-300">
                    DISCORD_CLIENT_ID
                  </span>{" "}
                  ve{" "}
                  <span className="text-amber-300">
                    DISCORD_CLIENT_SECRET
                  </span>{" "}
                  ayarlaması gerekiyor.
                </div>
              )}

              <div className="flex items-center gap-3 text-[10px] font-mono text-zinc-600">
                <div className="flex-1 h-px bg-zinc-800" />
                VEYA
                <div className="flex-1 h-px bg-zinc-800" />
              </div>

              <Button
                onClick={() => setStep("invite")}
                variant="outline"
                data-testid="mock-flow-button"
                className="w-full border-zinc-800 bg-black/30 hover:bg-zinc-900/80 h-11 text-zinc-300"
              >
                Davet Kodu ile Giriş
              </Button>

              {/* <div className="pt-4 border-t border-zinc-900 text-center">
                <button
                  onClick={() => setStep("bootstrap")}
                  data-testid="bootstrap-link"
                  className="text-xs text-zinc-500 hover:text-rose-400 font-mono tracking-wider transition"
                >
                  // İLK KURULUM (KURUCU)
                </button>
              </div> */}

            </Card>
          )}

          {/* =====================================================
              INVITE
          ====================================================== */}

          {step === "invite" && (
            <Card className="bg-zinc-950/10 backdrop-blur-xl border-white/10 shadow-2xl shadow-black/10 p-7 md:p-8 space-y-6">

              <div>
                <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">
                  // STEP 01 · Davet Kodu
                </div>

                <h2 className="font-heading text-3xl font-bold tracking-tight text-white">
                  Davet Kodu
                </h2>

                <p className="text-sm text-zinc-500 mt-2">
                  Yetkiliden aldığın kodu gir.
                </p>
              </div>

              <div className="space-y-3">

                <Label className="text-xs font-mono text-zinc-400 tracking-wider">
                  DAVET KODU
                </Label>

                <Input
                  value={inviteCode}
                  onChange={(e) =>
                    setInviteCode(e.target.value.toUpperCase())
                  }
                  placeholder="XXXXXXXX"
                  data-testid="invite-code-input"
                  className="bg-zinc-900/80 border-zinc-800 font-mono tracking-widest text-center text-lg h-12 focus-visible:ring-rose-500"
                />

              </div>

              <Button
                onClick={verifyInvite}
                disabled={loading}
                data-testid="verify-invite-button"
                className="w-full bg-rose-600 hover:bg-rose-700 h-11 font-semibold"
              >
                {loading && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}

                Kodu Doğrula
              </Button>

              <button
                onClick={() => setStep("main")}
                className="text-xs text-zinc-500 hover:text-rose-400 font-mono"
              >
                ← geri
              </button>

            </Card>
          )}

          {/* =====================================================
              DISCORD
          ====================================================== */}

          {step === "discord" && (
            <Card className="bg-zinc-950/10 backdrop-blur-xl border-white/10 shadow-2xl shadow-black/10 p-7 md:p-8 space-y-6">

              <div>
                <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">
                  // STEP 02 · MOCK
                </div>

                <h2 className="font-heading text-3xl font-bold tracking-tight text-white">
                  Mock Discord
                </h2>

                <p className="text-sm text-zinc-500 mt-2">
                  Test için kimlik gir. Gerçek Discord OAuth ana
                  ekrandan.
                </p>
              </div>

              <div className="space-y-3">

                <Label className="text-xs font-mono text-zinc-400 tracking-wider">
                  DISCORD ID
                </Label>

                <Input
                  value={mockDiscordId}
                  onChange={(e) =>
                    setMockDiscordId(e.target.value)
                  }
                  placeholder="123456789012345678"
                  data-testid="discord-id-input"
                  className="bg-zinc-900/80 border-zinc-800 font-mono focus-visible:ring-rose-500"
                />

                <Label className="text-xs font-mono text-zinc-400 tracking-wider">
                  KULLANICI ADI
                </Label>

                <Input
                  value={mockUsername}
                  onChange={(e) =>
                    setMockUsername(e.target.value)
                  }
                  placeholder="shadowuser"
                  data-testid="discord-username-input"
                  className="bg-zinc-900/80 border-zinc-800 focus-visible:ring-rose-500"
                />

              </div>

              <Button
                onClick={mockDiscordLogin}
                disabled={loading}
                data-testid="mock-discord-login-button"
                className="w-full bg-[#5865F2] hover:bg-[#4752c4] h-11 font-semibold"
              >
                {loading && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}

                <Shield className="w-4 h-4 mr-2" />

                Mock ile Devam Et
              </Button>

              <button
                onClick={() => setStep("invite")}
                className="text-xs text-zinc-500 hover:text-rose-400 font-mono"
              >
                ← geri
              </button>

            </Card>
          )}

          {/* =====================================================
              HEX
          ====================================================== */}

          {step === "hex" && (
            <Card className="bg-zinc-950/10 backdrop-blur-xl border-white/10 shadow-2xl shadow-black/10 p-7 md:p-8 space-y-6">

              <div>
                <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">
                  // STEP 03 · MOCK
                </div>

                <h2 className="font-heading text-3xl font-bold tracking-tight text-white">
                  Hex ID
                </h2>

                <p className="text-sm text-zinc-500 mt-2">
                  Karakter kimliğin sisteme kayıt edilecek.
                </p>
              </div>

              <div className="space-y-3">

                <Label className="text-xs font-mono text-zinc-400 tracking-wider">
                  HEX ID
                </Label>

                <Input
                  value={hexId}
                  onChange={(e) => setHexId(e.target.value)}
                  placeholder="steam:110000..."
                  data-testid="hex-id-input"
                  className="bg-zinc-900/80 border-zinc-800 font-mono focus-visible:ring-rose-500"
                />

                <Label className="text-xs font-mono text-zinc-400 tracking-wider">
                  GÖSTERİM ADI (opsiyonel)
                </Label>

                <Input
                  value={displayName}
                  onChange={(e) =>
                    setDisplayName(e.target.value)
                  }
                  placeholder="Karakter adı"
                  data-testid="display-name-input"
                  className="bg-zinc-900/80 border-zinc-800 focus-visible:ring-rose-500"
                />

              </div>

              <div className="text-[11px] font-mono text-zinc-500 bg-zinc-900/70 p-3 border-l-2 border-rose-500">
                Başlangıç rolü:{" "}
                <span className="text-rose-400">
                  {inviteRole}
                </span>
              </div>

              <Button
                onClick={completeRegistration}
                disabled={loading}
                data-testid="complete-register-button"
                className="w-full bg-rose-600 hover:bg-rose-700 h-11 font-semibold"
              >
                {loading && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}

                <Fingerprint className="w-4 h-4 mr-2" />

                Kaydı Tamamla
              </Button>

            </Card>
          )}

          {/* =====================================================
              BOOTSTRAP
          ====================================================== */}

          {step === "bootstrap" && (
            <Card className="bg-zinc-950/90 backdrop-blur-xl border-amber-900/50 shadow-2xl shadow-black/10 p-7 md:p-8 space-y-6">

              <div>
                <div className="text-[11px] font-mono text-amber-500 tracking-widest mb-2">
                  // FOUNDER SETUP
                </div>

                <h2 className="font-heading text-3xl font-bold tracking-tight text-white">
                  İlk Kurulum
                </h2>

                <p className="text-sm text-zinc-400 mt-2">
                  {needsFounder
                    ? "Sistemde henüz kurucu yok. İlk kurucu hesabını sen oluştur — davet koduna gerek yok."
                    : "Sadece sistem boşken çalışır. Kurucu hesabını oluştur."}
                </p>
              </div>

              <div className="space-y-3">

                <Label className="text-xs font-mono text-zinc-400 tracking-wider">
                  DISCORD ID
                </Label>

                <Input
                  value={mockDiscordId}
                  onChange={(e) =>
                    setMockDiscordId(e.target.value)
                  }
                  data-testid="bootstrap-discord-id"
                  className="bg-zinc-900/80 border-zinc-800 font-mono"
                />

                <Label className="text-xs font-mono text-zinc-400 tracking-wider">
                  KULLANICI ADI
                </Label>

                <Input
                  value={mockUsername}
                  onChange={(e) =>
                    setMockUsername(e.target.value)
                  }
                  data-testid="bootstrap-username"
                  className="bg-zinc-900/80 border-zinc-800"
                />

                <Label className="text-xs font-mono text-zinc-400 tracking-wider">
                  HEX ID
                </Label>

                <Input
                  value={hexId}
                  onChange={(e) => setHexId(e.target.value)}
                  data-testid="bootstrap-hex-id"
                  className="bg-zinc-900/80 border-zinc-800 font-mono"
                />

              </div>

              <Button
                onClick={bootstrapFounder}
                disabled={loading}
                data-testid="bootstrap-submit-button"
                className="w-full bg-amber-600 hover:bg-amber-700 h-11 font-semibold text-black"
              >
                {loading && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}

                <KeyRound className="w-4 h-4 mr-2" />

                Kurucu Olarak Kur
              </Button>

              {!needsFounder && (
                <button
                  onClick={() => setStep("main")}
                  className="text-xs text-zinc-500 hover:text-rose-400 font-mono"
                >
                  ← geri
                </button>
              )}

            </Card>
          )}

        </div>
      </div>

      {/* =========================================================
          VIDEO SOUND CONTROL
      ========================================================== */}

      <div className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-30">

        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-black/65 backdrop-blur-xl border border-white/10 shadow-xl">

          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Sesi aç" : "Sesi kapat"}
            className="text-white hover:text-rose-400 transition-colors"
          >
            {muted ? (
              <VolumeX className="w-5 h-5" />
            ) : (
              <Volume2 className="w-5 h-5" />
            )}
          </button>

          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume}
            onChange={handleVolumeChange}
            aria-label="Video ses seviyesi"
            className="w-20 md:w-28 accent-rose-600 cursor-pointer"
          />

          <span className="hidden sm:block text-[10px] font-mono text-zinc-500 min-w-[30px] text-right">
            {Math.round((muted ? 0 : volume) * 100)}%
          </span>

        </div>

      </div>

      {/* =========================================================
          BOTTOM STATUS
      ========================================================== */}

      <div className="fixed bottom-6 left-6 md:bottom-8 md:left-8 z-20 hidden md:block">
        <div className="flex items-center gap-2 text-[11px] font-mono text-zinc-500 tracking-widest">

          <div className="w-1.5 h-1.5 rounded-full bg-rose-500 shadow-sm shadow-rose-500/50" />

          DEULSRA // SECURE ACCESS // v2.1.4 // 2026 // <span className="text-zinc-400">by cв</span>

        </div>
      </div>

    </div>
  );
}
